import { IDashboardStats } from "../../models/DashboardStats";
import PriceFormat from "../../utils/priceFormat";
import DashboardTile from "./DashboardTile";

interface IVehicleManagementTableProps {
  dashboardStats: IDashboardStats | undefined;
  span?: 1 | 2;
}

type DashboardStatsExtended = IDashboardStats & {
  totalVehiclesPriceToday?: number;
  averageVehiclesPriceToday?: number;
};

const rowConfig: { key: keyof DashboardStatsExtended; label: string; format?: "number" | "price"; bgClass: string; textClass: string }[] = [
  { key: "collectionsToday", label: "Collections Today", format: "number", bgClass: "bg-orange-50", textClass: "text-orange-700" },
  { key: "dropOffsToday", label: "Drop offs Today", format: "number", bgClass: "bg-red-50", textClass: "text-red-700" },
  { key: "totalVehiclesToday", label: "Total Vehicles Today", format: "number", bgClass: "bg-teal-50", textClass: "text-teal-700" },
  { key: "totalVehiclesPriceToday", label: "Total Price of Vehicles Today", format: "price", bgClass: "bg-emerald-50", textClass: "text-emerald-700" },
  { key: "averageVehiclesPriceToday", label: "Average Vehicle Price", format: "price", bgClass: "bg-indigo-50", textClass: "text-indigo-700" },
];

export default function VehicleManagementTable({ dashboardStats, span = 2 }: IVehicleManagementTableProps) {
  if (!dashboardStats) return null;

  const stats = dashboardStats as DashboardStatsExtended;

  const formatValue = (key: string, value: number | null | undefined): string => {
    const row = rowConfig.find((r) => r.key === key);
    if (value == null || Number.isNaN(value)) return "—";
    if (row?.format === "price") return PriceFormat(value);
    return String(value);
  };

  return (
    <DashboardTile span={span}>
      <div className="p-3">
        <div className="mb-3 w-full">
          <h3 className="prose font-semibold text-gray-900">Vehicle Management</h3>
          <p className="text-sm text-gray-500 mt-0.5">Today&apos;s vehicle activity at a glance</p>
        </div>

        <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
          <table className="w-full border-collapse text-left text-sm">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-200">
                <th className="pb-2.5 pt-2.5 pl-4 pr-4 font-semibold text-gray-800">Metric</th>
                <th className="pb-2.5 pt-2.5 pr-4 text-right font-semibold text-gray-800">Value</th>
              </tr>
            </thead>
            <tbody>
              {rowConfig.map(({ key, label, bgClass, textClass }) => {
                const value = stats[key] as number | null | undefined;
                const displayValue = formatValue(String(key), value);
                return (
                  <tr key={String(key)} className={`font-medium transition-colors ${bgClass}`}>
                    <td className={`py-2.5 pl-4 pr-4 ${textClass}`}>{label}</td>
                    <td className={`py-2.5 pr-4 text-right font-bold ${textClass}`}>{displayValue}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardTile>
  );
}
